import React, { FC } from 'react';

import { SwiperOptions } from 'swiper';
import Slider from 'components/commercetools-ui/atoms/slider';
import Typography from 'components/commercetools-ui/atoms/typography';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import { desktop, hugeDesktop, largeDesktop, minitablet, mobile, tablet } from 'helpers/utils/screensizes';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { useParams, usePathname } from 'next/navigation';
import { Configure, useHits } from 'react-instantsearch-hooks-web';
import { mapProduct } from 'helpers/algolia/map-product';
import { breakpoints } from 'helpers/utils/breakpoints';
import ProductTiles from '../product-tile/tile';
import { NationalBook, Tiempos } from 'fonts';
import { isPdpUrl } from 'helpers/utils/pdpUrlHelper';

export interface ProductTileCMSProps extends Partial<SwiperOptions> {
  data: any;
}

const ProductTilesCMS: FC<ProductTileCMSProps> = ({ data }) => {
  const [isMiniTabletSize] = useMediaQuery(minitablet);
  const [isTabletSize] = useMediaQuery(tablet);
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isHugeDesktop] = useMediaQuery(hugeDesktop);

  const currentURL = usePathname();
  const { locale } = useParams();
  const { hits } = useHits();

  const isProductDetailPage = isPdpUrl(currentURL);
  const productSection = data?.sections?.find((section: any) => section?.commercetools_product) 
  const rawSkuCodes = productSection?.commercetools_product?.sku_code;
  const Title = productSection?.commercetools_product?.title;




  const skuCodes = rawSkuCodes
  ?.match(/<p>(.*?)<\/p>/g)
  ?.map((code: string) => code.replace(/<\/?p>/g, '').trim());
  const getFilterQuery = (skus: string[]) => {
    const query = skus
      .map(sku => `variants.searchableAttributes.item-code:'${sku}'`)
      .join(' OR ');

    return query;
  };

  const mappedProducts = hits.map((hit: any) => {
    const matchedSku = skuCodes.find((code: any) => 
      hit.variants.some((variant: any) => variant.attributes['item-code'] === code)
    );

    if (!matchedSku) {
      return null; 
    }

    return mapProduct(hit, locale, matchedSku);
  }).filter((product: any) => product !== null); 

  const sortedProducts = mappedProducts.slice().sort((productA: any, productB: any) => {
    const itemCodeA = productA.productcode || '';
    const itemCodeB = productB.productcode || '';

    const indexA = skuCodes.indexOf(itemCodeA);
    const indexB = skuCodes.indexOf(itemCodeB);

    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });

  return (
    <div
      id="related-products"
      className={`py-48 pl-24 xl:pb-80 ${hits.length > 1 ? '2xl:pb-178' : '2xl:pb-120'} xl:pl-60 2xl:pl-80 md:pl-28 lg:pl-48 md:py-48 xl:pt-60 2xl:pt-120 ${
        isProductDetailPage
          ? 'relative left-full w-screen -translate-x-full bg-[#EDEDED] px-48 md:pb-1 md:pt-100 lg:grid lg:grid-cols-12 lg:pl-0 xl:pb-153 xl:pt-153'
          : 'md:pl-40'
      }`}
    >
      <Configure query={''} filters={getFilterQuery(skuCodes)} hitsPerPage={48} facets={['*']} maxValuesPerFacet={999} 
          attributesToHighlight={[]} // Disable highlighting
          attributesToSnippet={[]} // Disable snippetting
          attributesToRetrieve={[
            "name",
            "slug",
            "categoryKeys",
            "masterVariantID",
            "searchableAttributes.IsNew",
            "variants.id",
            "variants.key",
            "variants.sku",
            "variants.images",
            "variants.attributes.colour",
            "variants.attributes.item-code",
            "variants.attributes.item-name",
          ]}          
      />
      <div className={isProductDetailPage ? 'lg:col-span-9 lg:col-start-4' : ''}>
        <Typography
          id="related-products-title"
          className={`pb-52 lg:pb-36 text-32 font-medium text-primary-black xl:text-36 2xl:text-48 ${Tiempos.className}`}
          as="h3"
        >
          {Title}
        </Typography>
        <div className="product-slider-container relative w-full">
          <Slider
            className="mb-48"
            slidesPerView={1.2}
            slidesPerGroup={1}
            dots={false}
            arrows={sortedProducts.length > 1 && (isTabletSize || isDesktopSize || isMiniTabletSize) && (!(isDesktopSize && sortedProducts.length <=3)) && (!(isHugeDesktop && sortedProducts.length <=4))}
            innerArrows
            autoplay={false}
            solidArrows
            allowArrowsOnTouchDevice
            nextButtonStyles={{ top: '110%', left: 50, padding: '10px' }}
            prevButtonStyles={{ top: '110%', left: 0, padding: '10px' }}
            allowTouchMove
            spaceBetween={8}
            breakpoints={{
              [mobile]: {
                slidesPerView: 2.1,
                spaceBetween: 16,
                ...(breakpoints[mobile] ?? {}),
              },
              [tablet]: {
                slidesPerView: 2.1,
                spaceBetween: 16,
                ...(breakpoints[tablet] ?? {}),
              },
              [desktop]: {
                slidesPerView: 3.5,
                spaceBetween: 16,
                ...(breakpoints[desktop] ?? {}),
              },
              [hugeDesktop]: {
                slidesPerView: 4.3,
                spaceBetween: 16,
                ...(breakpoints[hugeDesktop] ?? {}),
              },
              [largeDesktop]: {
                slidesPerView: 3.5,
                spaceBetween: 16,
                ...(breakpoints[largeDesktop] ?? {}),
              },
            }}
          >
            {sortedProducts.map((product: any, index: number) => (
              <ProductTiles
                key={index}
                images={product.images}
                product={product}
              />
            ))}
          </Slider>
        </div>
      </div>
    </div>
  );
};

export default ProductTilesCMS;
